import React, { useState, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import NavigationBar from '../../components/ui/NavigationBar';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useAuth } from '../../contexts/AuthContext';

// ─── Mock data ─────────────────────────────────────────────────────────────────
const MOCK_ACCOUNTS = [
  {
    id: 'acc_001',
    companyName: 'Apex Digital Agency',
    contactName: 'Sarah Chen',
    email: 'sarah@apexdigital.io',
    plan: 'Professional',
    status: 'active',
    customDomain: 'forms.apexdigital.io',
    primaryColor: '#2563eb',
    formsUsed: 18,
    formsLimit: 50,
    submissions: 4820,
    createdAt: '2026-01-12',
    lastActive: '2026-08-04',
    portalUrl: 'https://forms.apexdigital.io',
  },
  {
    id: 'acc_002',
    companyName: 'Momentum Marketing',
    contactName: 'James Okafor',
    email: 'james@momentummarketing.co',
    plan: 'Starter',
    status: 'active',
    customDomain: '',
    primaryColor: '#7c3aed',
    formsUsed: 6,
    formsLimit: 15,
    submissions: 1230,
    createdAt: '2026-03-05',
    lastActive: '2026-08-03',
    portalUrl: '',
  },
  {
    id: 'acc_003',
    companyName: 'Clearview Consulting',
    contactName: 'Priya Nair',
    email: 'priya@clearviewconsult.com',
    plan: 'Enterprise',
    status: 'pending',
    customDomain: 'forms.clearviewconsult.com',
    primaryColor: '#059669',
    formsUsed: 0,
    formsLimit: 200,
    submissions: 0,
    createdAt: '2026-08-01',
    lastActive: null,
    portalUrl: '',
  },
  {
    id: 'acc_004',
    companyName: 'Bright Spark Studio',
    contactName: 'Leo Martins',
    email: 'leo@brightspark.studio',
    plan: 'Professional',
    status: 'suspended',
    customDomain: '',
    primaryColor: '#d97706',
    formsUsed: 22,
    formsLimit: 50,
    submissions: 9100,
    createdAt: '2025-11-20',
    lastActive: '2026-07-15',
    portalUrl: '',
  },
];

const PLAN_OPTIONS = ['Starter', 'Professional', 'Enterprise'];

const STATUS_STYLES = {
  active: 'bg-success/10 text-success',
  pending: 'bg-warning/10 text-warning',
  suspended: 'bg-error/10 text-error',
};

// ─── Helpers ───────────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium capitalize ${STATUS_STYLES?.[status] || 'bg-muted text-muted-foreground'}`}>
    <span className={`w-1.5 h-1.5 rounded-full ${status === 'active' ? 'bg-success' : status === 'pending' ? 'bg-warning' : 'bg-error'}`} />
    {status}
  </span>
);

const PlanBadge = ({ plan }) => {
  const colors = {
    Starter: 'bg-muted text-muted-foreground',
    Professional: 'bg-primary/10 text-primary',
    Enterprise: 'bg-purple-500/10 text-purple-600',
  };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${colors?.[plan] || 'bg-muted text-muted-foreground'}`}>
      {plan}
    </span>
  );
};

const UsageBar = ({ used, limit }) => {
  const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  const color = pct >= 90 ? 'bg-error' : pct >= 70 ? 'bg-warning' : 'bg-primary';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-xs text-muted-foreground whitespace-nowrap">{used}/{limit}</span>
    </div>
  );
};

// ─── Create / Edit Account Modal ───────────────────────────────────────────────
const AccountModal = ({ account, onClose, onSave }) => {
  const isEdit = !!account?.id;
  const [form, setForm] = useState({
    companyName: account?.companyName || '',
    contactName: account?.contactName || '',
    email: account?.email || '',
    plan: account?.plan || 'Professional',
    customDomain: account?.customDomain || '',
    primaryColor: account?.primaryColor || '#107570',
    sendInvite: !isEdit,
  });
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form?.companyName?.trim()) e.companyName = 'Company name is required';
    if (!form?.contactName?.trim()) e.contactName = 'Contact name is required';
    if (!form?.email?.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(form?.email)) e.email = 'Valid email is required';
    setErrors(e);
    return Object.keys(e)?.length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    await new Promise((r) => setTimeout(r, 800));
    onSave({ ...account, ...form, id: account?.id || `acc_${Date.now()}`, status: account?.status || 'pending', formsUsed: account?.formsUsed || 0, formsLimit: form?.plan === 'Starter' ? 15 : form?.plan === 'Professional' ? 50 : 200, submissions: account?.submissions || 0, createdAt: account?.createdAt || new Date()?.toISOString()?.split('T')?.[0], lastActive: account?.lastActive || null });
    setSaving(false);
    onClose();
  };

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name={isEdit ? 'UserCog' : 'UserPlus'} size={16} color="var(--color-primary)" />
            </div>
            <h2 className="text-base font-heading font-semibold text-foreground">
              {isEdit ? 'Edit Account' : 'Create Customer Account'}
            </h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-muted transition-colors">
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Company */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Company Name <span className="text-error">*</span></label>
            <Input value={form?.companyName} onChange={(e) => set('companyName', e?.target?.value)} placeholder="Acme Agency" />
            {errors?.companyName && <p className="text-xs text-error">{errors?.companyName}</p>}
          </div>

          {/* Contact */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Contact Name <span className="text-error">*</span></label>
              <Input value={form?.contactName} onChange={(e) => set('contactName', e?.target?.value)} placeholder="Jane Smith" />
              {errors?.contactName && <p className="text-xs text-error">{errors?.contactName}</p>}
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Email <span className="text-error">*</span></label>
              <Input type="email" value={form?.email} onChange={(e) => set('email', e?.target?.value)} placeholder="jane@agency.com" />
              {errors?.email && <p className="text-xs text-error">{errors?.email}</p>}
            </div>
          </div>

          {/* Plan */}
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-foreground">Plan</label>
            <div className="flex gap-2">
              {PLAN_OPTIONS?.map((p) => (
                <button key={p} onClick={() => set('plan', p)}
                  className={`flex-1 py-2 px-3 rounded-lg border text-sm font-medium transition-colors ${form?.plan === p ? 'border-primary bg-primary/10 text-primary' : 'border-border bg-background text-foreground hover:bg-muted'}`}>
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Branding */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Custom Domain</label>
              <Input value={form?.customDomain} onChange={(e) => set('customDomain', e?.target?.value)} placeholder="forms.agency.com" />
              <p className="text-xs text-muted-foreground">Optional CNAME subdomain</p>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">Brand Color</label>
              <div className="flex items-center gap-2">
                <input type="color" value={form?.primaryColor} onChange={(e) => set('primaryColor', e?.target?.value)}
                  className="w-10 h-10 rounded-md border border-border cursor-pointer bg-transparent p-0.5 flex-shrink-0" />
                <Input value={form?.primaryColor} onChange={(e) => set('primaryColor', e?.target?.value)}
                  placeholder="#107570" className="font-mono text-sm" maxLength={7} />
              </div>
            </div>
          </div>

          {/* Send invite toggle */}
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border">
            <div>
              <p className="text-sm font-medium text-foreground">Send portal invitation email</p>
              <p className="text-xs text-muted-foreground mt-0.5">Customer receives login link and onboarding instructions</p>
            </div>
            <button type="button" onClick={() => set('sendInvite', !form?.sendInvite)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${form?.sendInvite ? 'bg-primary' : 'bg-muted-foreground/30'}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${form?.sendInvite ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}
            iconName={saving ? 'Loader2' : isEdit ? 'Save' : 'UserPlus'} iconPosition="left">
            {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Account'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Bulk Import Modal ─────────────────────────────────────────────────────────
const BulkImportModal = ({ onClose, onImport }) => {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState('');

  const parseCSV = (text) => {
    const lines = text?.trim()?.split('\n');
    if (lines?.length < 2) return [];
    const headers = lines?.[0]?.split(',')?.map((h) => h?.trim()?.toLowerCase()?.replace(/\s+/g, '_'));
    return lines?.slice(1)?.map((line) => {
      const vals = line?.split(',')?.map((v) => v?.trim()?.replace(/^"|"$/g, ''));
      const obj = {};
      headers?.forEach((h, i) => { obj[h] = vals?.[i] || ''; });
      return obj;
    })?.filter((r) => r?.email || r?.company_name);
  };

  const handleFile = (f) => {
    if (!f) return;
    if (!f?.name?.endsWith('.csv')) { setError('Only CSV files are supported.'); return; }
    setError('');
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      const rows = parseCSV(e?.target?.result);
      setPreview(rows?.slice(0, 5));
    };
    reader?.readAsText(f);
  };

  const handleDrop = (e) => {
    e?.preventDefault();
    setDragOver(false);
    handleFile(e?.dataTransfer?.files?.[0]);
  };

  const handleImport = async () => {
    if (!file) return;
    setImporting(true);
    await new Promise((r) => setTimeout(r, 1200));
    onImport(preview?.length);
    setImporting(false);
    onClose();
  };

  const downloadTemplate = () => {
    const csv = 'company_name,contact_name,email,plan,custom_domain,primary_color\nAcme Agency,Jane Smith,jane@acme.com,Professional,forms.acme.com,#107570\n';
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'wl_accounts_template.csv'; a?.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Upload" size={16} color="var(--color-primary)" />
            </div>
            <h2 className="text-base font-heading font-semibold text-foreground">Bulk Import Accounts</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-muted transition-colors">
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Template download */}
          <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border">
            <div>
              <p className="text-sm font-medium text-foreground">CSV Template</p>
              <p className="text-xs text-muted-foreground">Download the template with required columns</p>
            </div>
            <Button variant="outline" size="sm" iconName="Download" iconPosition="left" onClick={downloadTemplate}>
              Template
            </Button>
          </div>

          {/* Drop zone */}
          <div
            onDragOver={(e) => { e?.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            onClick={() => fileRef?.current?.click()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/30'}`}
          >
            <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={(e) => handleFile(e?.target?.files?.[0])} />
            <Icon name={file ? 'FileCheck2' : 'FileUp'} size={32} color={file ? 'var(--color-success)' : 'var(--color-muted-foreground)'} className="mx-auto mb-3" />
            {file ? (
              <>
                <p className="text-sm font-medium text-foreground">{file?.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{preview?.length} accounts detected (showing first 5)</p>
              </>
            ) : (
              <>
                <p className="text-sm font-medium text-foreground">Drop your CSV file here</p>
                <p className="text-xs text-muted-foreground mt-1">or click to browse · CSV only</p>
              </>
            )}
          </div>

          {error && <p className="text-xs text-error flex items-center gap-1.5"><Icon name="AlertCircle" size={13} />{error}</p>}

          {/* Preview table */}
          {preview?.length > 0 && (
            <div className="border border-border rounded-lg overflow-hidden">
              <div className="px-4 py-2 bg-muted border-b border-border">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Preview (first {preview?.length} rows)</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border">
                      {['Company', 'Contact', 'Email', 'Plan']?.map((h) => (
                        <th key={h} className="px-3 py-2 text-left font-medium text-muted-foreground">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {preview?.map((row, i) => (
                      <tr key={i} className="border-b border-border last:border-0">
                        <td className="px-3 py-2 text-foreground truncate max-w-[100px]">{row?.company_name || '—'}</td>
                        <td className="px-3 py-2 text-foreground truncate max-w-[80px]">{row?.contact_name || '—'}</td>
                        <td className="px-3 py-2 text-muted-foreground truncate max-w-[120px]">{row?.email || '—'}</td>
                        <td className="px-3 py-2 text-foreground">{row?.plan || 'Professional'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={handleImport} disabled={!file || importing}
            iconName={importing ? 'Loader2' : 'Upload'} iconPosition="left">
            {importing ? 'Importing…' : `Import${preview?.length ? ` ${preview?.length} Accounts` : ''}`}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Invite Modal ──────────────────────────────────────────────────────────────
const InviteModal = ({ account, onClose }) => {
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [customMsg, setCustomMsg] = useState('');

  const handleSend = async () => {
    setSending(true);
    await new Promise((r) => setTimeout(r, 900));
    setSending(false);
    setSent(true);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Mail" size={16} color="var(--color-primary)" />
            </div>
            <h2 className="text-base font-heading font-semibold text-foreground">Send Portal Invitation</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-muted transition-colors">
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {sent ? (
            <div className="text-center py-6">
              <div className="w-14 h-14 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircle2" size={28} color="var(--color-success)" />
              </div>
              <p className="text-base font-semibold text-foreground">Invitation sent!</p>
              <p className="text-sm text-muted-foreground mt-1">Portal access email delivered to <strong>{account?.email}</strong></p>
            </div>
          ) : (
            <>
              <div className="p-3 bg-muted/50 rounded-lg border border-border space-y-1">
                <p className="text-sm font-medium text-foreground">{account?.companyName}</p>
                <p className="text-xs text-muted-foreground">{account?.contactName} · {account?.email}</p>
              </div>
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-foreground">Custom message <span className="text-muted-foreground font-normal">(optional)</span></label>
                <textarea rows={3} value={customMsg} onChange={(e) => setCustomMsg(e?.target?.value)}
                  placeholder="Add a personal note to the invitation email…"
                  className="w-full px-3 py-2.5 text-sm bg-background border border-border rounded-md text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary resize-none transition-colors" />
              </div>
              <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
                <p className="text-xs text-primary font-medium flex items-center gap-1.5">
                  <Icon name="Info" size={13} />
                  The email will include their portal URL, login credentials, and white-label branding setup guide.
                </p>
              </div>
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <Button variant="outline" onClick={onClose}>{sent ? 'Close' : 'Cancel'}</Button>
          {!sent && (
            <Button variant="primary" onClick={handleSend} disabled={sending}
              iconName={sending ? 'Loader2' : 'Send'} iconPosition="left">
              {sending ? 'Sending…' : 'Send Invitation'}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// ─── Account Detail Drawer ─────────────────────────────────────────────────────
const AccountDetailDrawer = ({ account, onClose, onEdit, onInvite, onSuspend, onActivate }) => {
  if (!account) return null;
  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-card border-l border-border w-full max-w-sm h-full overflow-y-auto shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 bg-card border-b border-border px-5 py-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-sm font-bold"
              style={{ backgroundColor: account?.primaryColor }}>
              {account?.companyName?.[0]}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">{account?.companyName}</p>
              <StatusBadge status={account?.status} />
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-muted transition-colors flex-shrink-0">
            <Icon name="X" size={16} color="var(--color-muted-foreground)" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Contact info */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Contact</p>
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-sm text-foreground">
                <Icon name="User" size={14} color="var(--color-muted-foreground)" />
                {account?.contactName}
              </div>
              <div className="flex items-center gap-2 text-sm text-foreground">
                <Icon name="Mail" size={14} color="var(--color-muted-foreground)" />
                {account?.email}
              </div>
              {account?.customDomain && (
                <div className="flex items-center gap-2 text-sm text-foreground">
                  <Icon name="Globe" size={14} color="var(--color-muted-foreground)" />
                  {account?.customDomain}
                </div>
              )}
            </div>
          </div>

          {/* Plan & usage */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Plan & Usage</p>
            <div className="p-3 bg-muted/50 rounded-lg border border-border space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Plan</span>
                <PlanBadge plan={account?.plan} />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-muted-foreground">Forms used</span>
                </div>
                <UsageBar used={account?.formsUsed} limit={account?.formsLimit} />
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Total submissions</span>
                <span className="text-sm font-medium text-foreground">{account?.submissions?.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* Dates */}
          <div className="space-y-2">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Timeline</p>
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Created</span>
                <span className="text-foreground">{account?.createdAt}</span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Last active</span>
                <span className="text-foreground">{account?.lastActive || 'Never'}</span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-2 pt-2">
            <Button variant="outline" className="w-full" iconName="Pencil" iconPosition="left" onClick={() => onEdit(account)}>
              Edit Account
            </Button>
            <Button variant="outline" className="w-full" iconName="Send" iconPosition="left" onClick={() => onInvite(account)}>
              Send Invitation
            </Button>
            {account?.status === 'suspended' ? (
              <Button variant="outline" className="w-full text-success border-success/30 hover:bg-success/5" iconName="CheckCircle2" iconPosition="left" onClick={() => onActivate(account?.id)}>
                Reactivate Account
              </Button>
            ) : (
              <Button variant="outline" className="w-full text-error border-error/30 hover:bg-error/5" iconName="Ban" iconPosition="left" onClick={() => onSuspend(account?.id)}>
                Suspend Account
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────
const WhiteLabelAccounts = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [accounts, setAccounts] = useState(MOCK_ACCOUNTS);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlan, setFilterPlan] = useState('all');
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [editAccount, setEditAccount] = useState(null);
  const [inviteAccount, setInviteAccount] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const filtered = accounts?.filter((a) => {
    const matchSearch = !search || a?.companyName?.toLowerCase()?.includes(search?.toLowerCase()) || a?.email?.toLowerCase()?.includes(search?.toLowerCase()) || a?.contactName?.toLowerCase()?.includes(search?.toLowerCase());
    const matchStatus = filterStatus === 'all' || a?.status === filterStatus;
    const matchPlan = filterPlan === 'all' || a?.plan === filterPlan;
    return matchSearch && matchStatus && matchPlan;
  });

  const stats = {
    total: accounts?.length,
    active: accounts?.filter((a) => a?.status === 'active')?.length,
    pending: accounts?.filter((a) => a?.status === 'pending')?.length,
    suspended: accounts?.filter((a) => a?.status === 'suspended')?.length,
  };

  const handleSave = (acc) => {
    setAccounts((prev) => {
      const idx = prev?.findIndex((a) => a?.id === acc?.id);
      if (idx >= 0) { const next = [...prev]; next[idx] = acc; return next; }
      return [...prev, acc];
    });
    showToast(acc?.id ? 'Account updated successfully' : 'Account created and invitation sent');
  };

  const handleSuspend = (id) => {
    setAccounts((prev) => prev?.map((a) => a?.id === id ? { ...a, status: 'suspended' } : a));
    setSelectedAccount(null);
    showToast('Account suspended', 'warning');
  };

  const handleActivate = (id) => {
    setAccounts((prev) => prev?.map((a) => a?.id === id ? { ...a, status: 'active' } : a));
    setSelectedAccount(null);
    showToast('Account reactivated');
  };

  const handleBulkImport = (count) => {
    showToast(`${count} accounts imported and invitations queued`);
  };

  return (
    <>
      <Helmet>
        <title>White-Label Accounts — FormCraft</title>
      </Helmet>
      <NavigationBar stats={stats} />
      <div className="flex min-h-screen bg-background pt-16">
        {/* Sidebar spacer */}
        <div className="w-64 flex-shrink-0 hidden lg:block" />

        <main className="flex-1 min-w-0 p-6 lg:p-8">
          {/* Page header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
                <button onClick={() => navigate('/white-label-configuration')} className="hover:text-foreground transition-colors">White Label</button>
                <Icon name="ChevronRight" size={14} />
                <span className="text-foreground font-medium">Customer Accounts</span>
              </div>
              <h1 className="text-2xl font-heading font-bold text-foreground">Customer Accounts</h1>
              <p className="text-sm text-muted-foreground mt-1">Provision and manage white-label portal accounts for your clients</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button variant="outline" iconName="Upload" iconPosition="left" onClick={() => setShowBulkImport(true)}>
                Bulk Import
              </Button>
              <Button variant="primary" iconName="UserPlus" iconPosition="left" onClick={() => setShowCreate(true)}>
                New Account
              </Button>
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total Accounts', value: stats?.total, icon: 'Building2', color: 'text-foreground', bg: 'bg-muted/50' },
              { label: 'Active', value: stats?.active, icon: 'CheckCircle2', color: 'text-success', bg: 'bg-success/5' },
              { label: 'Pending Setup', value: stats?.pending, icon: 'Clock', color: 'text-warning', bg: 'bg-warning/5' },
              { label: 'Suspended', value: stats?.suspended, icon: 'Ban', color: 'text-error', bg: 'bg-error/5' },
            ]?.map((s) => (
              <div key={s?.label} className={`${s?.bg} border border-border rounded-xl p-4 flex items-center gap-3`}>
                <div className={`w-9 h-9 rounded-lg bg-card border border-border flex items-center justify-center flex-shrink-0`}>
                  <Icon name={s?.icon} size={18} color={`var(--color-${s?.color?.replace('text-', '')})`} />
                </div>
                <div>
                  <p className={`text-xl font-bold ${s?.color}`}>{s?.value}</p>
                  <p className="text-xs text-muted-foreground">{s?.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-3 mb-5">
            <div className="relative flex-1 max-w-sm">
              <Icon name="Search" size={16} color="var(--color-muted-foreground)" className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e?.target?.value)}
                placeholder="Search by company, contact, or email…"
                className="w-full pl-9 pr-4 py-2 text-sm bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
              />
            </div>
            <div className="flex gap-2">
              <select value={filterStatus} onChange={(e) => setFilterStatus(e?.target?.value)}
                className="px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors">
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="suspended">Suspended</option>
              </select>
              <select value={filterPlan} onChange={(e) => setFilterPlan(e?.target?.value)}
                className="px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors">
                <option value="all">All Plans</option>
                {PLAN_OPTIONS?.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          {/* Accounts table */}
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            {filtered?.length === 0 ? (
              <div className="py-16 text-center">
                <Icon name="Building2" size={40} color="var(--color-muted-foreground)" className="mx-auto mb-3" />
                <p className="text-base font-medium text-foreground">No accounts found</p>
                <p className="text-sm text-muted-foreground mt-1">Try adjusting your filters or create a new account</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      {['Customer', 'Plan', 'Status', 'Forms', 'Submissions', 'Last Active', '']?.map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {filtered?.map((acc) => (
                      <tr key={acc?.id} className="hover:bg-muted/20 transition-colors cursor-pointer"
                        onClick={() => setSelectedAccount(acc)}>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                              style={{ backgroundColor: acc?.primaryColor }}>
                              {acc?.companyName?.[0]}
                            </div>
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">{acc?.companyName}</p>
                              <p className="text-xs text-muted-foreground truncate">{acc?.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3"><PlanBadge plan={acc?.plan} /></td>
                        <td className="px-4 py-3"><StatusBadge status={acc?.status} /></td>
                        <td className="px-4 py-3 min-w-[120px]"><UsageBar used={acc?.formsUsed} limit={acc?.formsLimit} /></td>
                        <td className="px-4 py-3 text-sm text-foreground">{acc?.submissions?.toLocaleString()}</td>
                        <td className="px-4 py-3 text-sm text-muted-foreground whitespace-nowrap">{acc?.lastActive || '—'}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1" onClick={(e) => e?.stopPropagation()}>
                            <button onClick={() => { setEditAccount(acc); }}
                              className="p-1.5 rounded-md hover:bg-muted transition-colors" title="Edit">
                              <Icon name="Pencil" size={14} color="var(--color-muted-foreground)" />
                            </button>
                            <button onClick={() => setInviteAccount(acc)}
                              className="p-1.5 rounded-md hover:bg-muted transition-colors" title="Send invitation">
                              <Icon name="Send" size={14} color="var(--color-muted-foreground)" />
                            </button>
                            <button onClick={() => acc?.status === 'suspended' ? handleActivate(acc?.id) : handleSuspend(acc?.id)}
                              className="p-1.5 rounded-md hover:bg-muted transition-colors"
                              title={acc?.status === 'suspended' ? 'Reactivate' : 'Suspend'}>
                              <Icon name={acc?.status === 'suspended' ? 'CheckCircle2' : 'Ban'} size={14}
                                color={acc?.status === 'suspended' ? 'var(--color-success)' : 'var(--color-error)'} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Footer count */}
          {filtered?.length > 0 && (
            <p className="text-xs text-muted-foreground mt-3">
              Showing {filtered?.length} of {accounts?.length} accounts
            </p>
          )}
        </main>
      </div>
      {/* Modals */}
      {showCreate && (
        <AccountModal account={{}} onClose={() => setShowCreate(false)} onSave={handleSave} />
      )}
      {editAccount && (
        <AccountModal account={editAccount} onClose={() => setEditAccount(null)} onSave={handleSave} />
      )}
      {inviteAccount && (
        <InviteModal account={inviteAccount} onClose={() => setInviteAccount(null)} />
      )}
      {showBulkImport && (
        <BulkImportModal onClose={() => setShowBulkImport(false)} onImport={handleBulkImport} />
      )}
      {/* Detail drawer */}
      {selectedAccount && (
        <AccountDetailDrawer
          account={selectedAccount}
          onClose={() => setSelectedAccount(null)}
          onEdit={(acc) => { setSelectedAccount(null); setEditAccount(acc); }}
          onInvite={(acc) => { setSelectedAccount(null); setInviteAccount(acc); }}
          onSuspend={handleSuspend}
          onActivate={handleActivate}
        />
      )}
      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-[100] flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium transition-all
          ${toast?.type === 'warning' ? 'bg-warning/10 border-warning/30 text-warning' : toast?.type === 'error' ? 'bg-error/10 border-error/30 text-error' : 'bg-success/10 border-success/30 text-success'}`}>
          <Icon name={toast?.type === 'warning' ? 'AlertTriangle' : toast?.type === 'error' ? 'XCircle' : 'CheckCircle2'} size={16} />
          {toast?.msg}
        </div>
      )}
    </>
  );
};

export default WhiteLabelAccounts;
